import { useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';

// Mirrors PASSWORD_CHANGE_INTERVAL_MS on the server, which is what
// actually enforces it — this only lets the form say so up front.
const PASSWORD_CHANGE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

interface ChangePasswordFormProps {
  // The first-sign-in screen, where the current password is the
  // temporary one someone handed over.
  forced?: boolean;
  onChanged?: () => void;
}

/**
 * The one change-password form, used on Settings, My Profile and the
 * first-sign-in screen, so all three follow the same rules.
 */
const ChangePasswordForm = ({ forced = false, onChanged }: ChangePasswordFormProps) => {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Super Admins are never limited, and a staff reset gives one change
  // that doesn't wait.
  const limited = !!user && user.role !== UserRole.SUPER_ADMIN && !user.mustChangePassword;
  const nextAllowedAt =
    limited && user?.passwordChangedAt
      ? new Date(new Date(user.passwordChangedAt).getTime() + PASSWORD_CHANGE_INTERVAL_MS)
      : null;
  const waiting = nextAllowedAt !== null && nextAllowedAt > new Date();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (form.newPassword === form.currentPassword) {
      setError('Choose a new password that is different from the current one.');
      return;
    }

    setSaving(true);
    try {
      const result = await apiService.changePassword(form.currentPassword, form.newPassword);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed. Any other device signed in to this account has been signed out.');
      updateUser(result.user);
      onChanged?.();
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        {forced
          ? 'Your password was set by a staff member. Choose your own password to continue — enter the temporary password you were given as the current password.'
          : user?.role === UserRole.SUPER_ADMIN
            ? 'Change your password at any time.'
            : 'You can change your password once a week.'}
      </p>

      {waiting && nextAllowedAt && (
        <p
          style={{
            padding: '0.75rem',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--warning-text)',
            fontSize: '0.85rem'
          }}
        >
          You changed your password recently. You can change it again on <strong>{formatDate(nextAllowedAt)}</strong>.
          Forgot it in the meantime? Use "Forgot password" on the sign-in page.
        </p>
      )}

      {error && <p style={{ color: 'var(--error-text)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}
      {success && <p style={{ color: '#34D399', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{success}</p>}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={saving || waiting} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div className="form-group">
            <label htmlFor="cp-current">{forced ? 'Temporary Password' : 'Current Password'}</label>
            <input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="cp-new">New Password</label>
            <input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
              minLength={8}
              required
            />
            <small style={{ color: 'var(--text-secondary)' }}>At least 8 characters.</small>
          </div>
          <div className="form-group">
            <label htmlFor="cp-confirm">Confirm New Password</label>
            <input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              minLength={8}
              required
            />
          </div>
          <button className="btn btn-primary" type="submit">
            {saving ? 'Changing...' : forced ? 'Set My Password' : 'Change Password'}
          </button>
        </fieldset>
      </form>
    </div>
  );
};

export default ChangePasswordForm;
